import { usePageRefreshKey } from '../../../../context/PageRefreshContext';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import {
    getCashBankList,
    getCashBankEntries,
    getCashBankSummary,
} from '../../../../redux/actions/SocietyPortal/CashBankAction/CashBankAction';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { financialYears } from '../../../../consts/DropDownOptions';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import {
    SOCIETY_PORTAL,
    SOCIETY_PORTAL_ACCOUNTS_CASH_AND_BANK,
} from '../../../../Routing/routes';
import ViewJournalModal from '../Journal/ViewJournalModal';

const CATEGORIES = [
    { value: 'all', label: 'All Transactions' },
    { value: 'money_in', label: 'Money In' },
    { value: 'money_out', label: 'Money Out' },
];

const TRANSACTION_TYPES = [
    { value: 'all', label: 'All Types' },
    { value: 'opening', label: 'Opening' },
    { value: 'daily', label: 'Daily' },
    { value: 'closing', label: 'Closing' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'receipt', label: 'Receipt' },
    { value: 'payment', label: 'Payment' },
    { value: 'purchase', label: 'Purchase' },
    { value: 'advance', label: 'Advance' },
    { value: 'debitnote', label: 'Debit Note' },
    { value: 'creditnote', label: 'Credit Note' },
    { value: 'facility', label: 'Facility' },
    { value: 'facility_receipt', label: 'Facility Receipt' },
    { value: 'debit_receipt', label: 'Debit Receipt' },
];

const getCurrentFY = () => {
    const now = new Date();
    const year = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${year}-${year + 1}`;
};

const formatCurrency = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const maskAccountNumber = (num) => {
    const str = String(num || '');
    if (!str) return '****';
    return `****${str.slice(-4)}`;
};

const BankBook = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { accountId: glCodeParam } = useParams();
    const [urlSearchParams] = useSearchParams();
    const societyId = selectedSocietyStorer.getSocietyId();

    const glCode = decodeURIComponent(glCodeParam || '');
    const initialYear = urlSearchParams.get('year') || getCurrentFY();

    const {
        listData,
        entriesData,
        entriesLoading,
        summaryData,
        summaryLoading,
    } = useSelector(state => state.CashBankReducer || {});

    const [financialYear, setFinancialYear] = useState(initialYear);
    const [category, setCategory] = useState('all');
    const [transactionType, setTransactionType] = useState('all');
    const [search, setSearch] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    // Journal voucher currently opened via the row-click. Modal auto-fetches
    // the JV detail via getJournalDetails when this is non-null.
    const [viewJournalId, setViewJournalId] = useState(null);

    // Filters that are actually applied to the API call
    const [appliedFilters, setAppliedFilters] = useState({
        category: 'all',
        transactionType: 'all',
        search: '',
        fromDate: '',
        toDate: '',
    });

    // Find current account info from list (or fetch if missing)
    const accountInfo = useMemo(() => {
        return (listData || []).find(a => a.gl_code === glCode) || null;
    }, [listData, glCode]);

    // Ensure list is loaded for header info
    useEffect(() => {
        if (societyId && (!listData || listData.length === 0)) {
            dispatch(getCashBankList({ societyId, year: financialYear }));
        }
    }, [dispatch, societyId, listData, financialYear]);

    // Fetch summary + entries whenever applied filters or year/glCode change
    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        if (!societyId || !glCode) return;
        dispatch(getCashBankSummary({
            societyId,
            glCode,
            year: financialYear,
            fromDate: appliedFilters.fromDate || undefined,
            toDate: appliedFilters.toDate || undefined,
        }));
        dispatch(getCashBankEntries({
            societyId,
            glCode,
            year: financialYear,
            fromDate: appliedFilters.fromDate || undefined,
            toDate: appliedFilters.toDate || undefined,
            category: appliedFilters.category,
            transactionType: appliedFilters.transactionType,
            search: appliedFilters.search || undefined,
        }));
    }, [dispatch, societyId, glCode, financialYear, appliedFilters, refreshKey]);

    // Auto-apply for dropdowns and dates (search still requires Apply)
    useEffect(() => {
        setAppliedFilters(prev => ({
            ...prev,
            category,
            transactionType,
            fromDate,
            toDate,
        }));
    }, [category, transactionType, fromDate, toDate]);

    const handleApply = () => {
        setAppliedFilters({ category, transactionType, search, fromDate, toDate });
    };

    const handleClear = () => {
        setCategory('all');
        setTransactionType('all');
        setSearch('');
        setFromDate('');
        setToDate('');
        setAppliedFilters({ category: 'all', transactionType: 'all', search: '', fromDate: '', toDate: '' });
    };

    const refresh = () => {
        setAppliedFilters({ category, transactionType, search, fromDate, toDate });
    };

    const goBack = () => navigate(SOCIETY_PORTAL + SOCIETY_PORTAL_ACCOUNTS_CASH_AND_BANK);

    const opening = summaryData?.opening_balance || { amount: 0, type: null };
    const closing = summaryData?.closing_balance || { amount: 0, type: null };
    const moneyIn = parseFloat(summaryData?.money_in) || 0;
    const moneyOut = parseFloat(summaryData?.money_out) || 0;

    const isCash = accountInfo?.account_type === 'cash';

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen">
            {/* Header */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4 tw-mb-4">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                    <div className="tw-flex tw-items-center tw-gap-3">
                        <button
                            onClick={goBack}
                            className="tw-p-1.5 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500 tw-flex tw-items-center tw-justify-center"
                        >
                            <Icon icon="mdi:arrow-left" className="tw-text-lg" />
                        </button>
                        <div className={`tw-w-9 tw-h-9 tw-rounded-lg tw-bg-gradient-to-br ${isCash ? 'tw-from-amber-400 tw-to-orange-500' : 'tw-from-rose-500 tw-to-pink-600'} tw-flex tw-items-center tw-justify-center`}>
                            <Icon icon={isCash ? 'hugeicons:money-bag-02' : 'hugeicons:bank'} className="tw-text-white tw-text-base" />
                        </div>
                        <div>
                            <h1 className="tw-text-base tw-font-bold tw-text-gray-800">
                                {accountInfo?.name || glCode}
                                <span className="tw-text-gray-400 tw-font-normal"> — {isCash ? 'Cash Book' : 'Bank Book'}</span>
                            </h1>
                            <p className="tw-text-[11px] tw-text-gray-500 tw-mt-0.5">
                                GL Code: {glCode}
                                {accountInfo?.account_no && (
                                    <>
                                        <span className="tw-text-gray-300 tw-mx-1">|</span>
                                        A/c: {maskAccountNumber(accountInfo.account_no)}
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <SearchableDropdown
                            value={financialYear}
                            onChange={setFinancialYear}
                            options={financialYears}
                            placeholder="Financial Year"
                            clearable={false}
                            small
                        />
                        <button
                            onClick={refresh}
                            className="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-text-gray-500"
                            title="Refresh"
                        >
                            <Icon icon="mdi:refresh" className="tw-text-lg" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3 tw-mb-4">
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <span className="tw-text-[11px] tw-text-gray-500 tw-font-medium tw-uppercase tw-tracking-wide">Opening Balance</span>
                        <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-gray-100 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="hugeicons:bank" className="tw-text-gray-500 tw-text-sm" />
                        </div>
                    </div>
                    <p className="tw-text-lg tw-font-bold tw-text-gray-800">
                        {summaryLoading ? '...' : `₹ ${formatCurrency(opening.amount)}`}
                        {opening.type && <span className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-ml-1">{opening.type}</span>}
                    </p>
                </div>
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <span className="tw-text-[11px] tw-text-gray-500 tw-font-medium tw-uppercase tw-tracking-wide">Money In</span>
                        <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-emerald-50 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:trending-up" className="tw-text-emerald-500 tw-text-sm" />
                        </div>
                    </div>
                    <p className="tw-text-lg tw-font-bold tw-text-emerald-600">{summaryLoading ? '...' : `₹ ${formatCurrency(moneyIn)}`}</p>
                </div>
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <span className="tw-text-[11px] tw-text-gray-500 tw-font-medium tw-uppercase tw-tracking-wide">Money Out</span>
                        <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-rose-50 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="mdi:trending-down" className="tw-text-rose-500 tw-text-sm" />
                        </div>
                    </div>
                    <p className="tw-text-lg tw-font-bold tw-text-rose-600">{summaryLoading ? '...' : `₹ ${formatCurrency(moneyOut)}`}</p>
                </div>
                <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4">
                    <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                        <span className="tw-text-[11px] tw-text-gray-500 tw-font-medium tw-uppercase tw-tracking-wide">Closing Balance</span>
                        <div className="tw-w-8 tw-h-8 tw-rounded-full tw-bg-rose-50 tw-flex tw-items-center tw-justify-center">
                            <Icon icon="hugeicons:bank" className="tw-text-rose-500 tw-text-sm" />
                        </div>
                    </div>
                    <p className={`tw-text-lg tw-font-bold ${closing.type === 'Cr' ? 'tw-text-red-600' : 'tw-text-rose-600'}`}>
                        {summaryLoading ? '...' : `₹ ${formatCurrency(closing.amount)}`}
                        {closing.type && <span className="tw-text-xs tw-font-semibold tw-ml-1">{closing.type}</span>}
                    </p>
                </div>
            </div>

            {/* Filters */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-p-4 tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                    <Icon icon="mdi:filter-variant" className="tw-text-gray-400 tw-text-sm" />
                    <span className="tw-text-xs tw-font-semibold tw-text-gray-700">Filters</span>
                </div>
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-6 tw-gap-3 tw-items-end">
                    <div>
                        <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">Category</label>
                        <SearchableDropdown
                            value={category}
                            onChange={setCategory}
                            options={CATEGORIES}
                            placeholder="All"
                            clearable={false}
                            small
                        />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">Transaction Type</label>
                        <SearchableDropdown
                            value={transactionType}
                            onChange={setTransactionType}
                            options={TRANSACTION_TYPES}
                            placeholder="All Types"
                            clearable={false}
                            small
                        />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">From Date</label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                        />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">To Date</label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                        />
                    </div>
                    <div className="lg:tw-col-span-1">
                        <label className="tw-block tw-text-[10px] tw-font-medium tw-text-gray-500 tw-uppercase tw-mb-1">Search</label>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleApply(); }}
                            placeholder="Remarks, cheque, invoice..."
                            className="tw-w-full tw-px-2.5 tw-py-1.5 tw-text-xs tw-border tw-border-gray-200 tw-rounded-md focus:tw-outline-none focus:tw-border-rose-500"
                        />
                    </div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <button
                            onClick={handleApply}
                            className="tw-flex-1 tw-px-4 tw-py-1.5 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-rose-600 hover:tw-bg-rose-700 tw-transition-colors"
                        >
                            Apply
                        </button>
                        <button
                            onClick={handleClear}
                            className="tw-p-1.5 tw-rounded-md tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-transition-colors"
                            title="Clear"
                        >
                            <Icon icon="mdi:close" className="tw-text-base" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Transactions Table */}
            <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-100 tw-shadow-sm tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gradient-to-r tw-from-slate-50 tw-to-gray-50">
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Date</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">JV / Reff</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Type</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Remarks</th>
                                <th className="tw-text-left tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Reference</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Debit (₹)</th>
                                <th className="tw-text-right tw-px-4 tw-py-3 tw-font-bold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-text-[10px]">Credit (₹)</th>
                            </tr>
                        </thead>
                        <tbody className="tw-divide-y tw-divide-gray-50">
                            {entriesLoading ? (
                                <tr>
                                    <td colSpan={7} className="tw-text-center tw-py-12 tw-text-gray-400">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                            <div className="tw-animate-spin tw-rounded-full tw-h-6 tw-w-6 tw-border-2 tw-border-gray-200 tw-border-t-rose-500"></div>
                                            <span className="tw-text-xs">Loading transactions...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (entriesData && entriesData.length > 0) ? (
                                entriesData.map((entry) => (
                                    <tr
                                        key={entry.journal_id}
                                        onClick={() => entry.journal_id && setViewJournalId(entry.journal_id)}
                                        className="hover:tw-bg-slate-50/50 tw-transition-colors tw-align-top tw-cursor-pointer"
                                        title="Click to view journal voucher"
                                    >
                                        <td className="tw-px-4 tw-py-3 tw-text-gray-600 tw-whitespace-nowrap">{entry.date}</td>
                                        <td className="tw-px-4 tw-py-3 tw-text-rose-600 tw-font-medium tw-whitespace-nowrap">
                                            {entry.jv_no || entry.reff_no || `#${entry.journal_id}`}
                                        </td>
                                        <td className="tw-px-4 tw-py-3">
                                            <span className="tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-rose-50 tw-text-rose-600 tw-text-[10px] tw-font-medium tw-border tw-border-rose-100 tw-capitalize">
                                                {entry.transaction_type || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="tw-px-4 tw-py-3 tw-text-gray-700 tw-leading-relaxed" style={{ minWidth: '300px' }}>{entry.remarks || '—'}</td>
                                        <td className="tw-px-4 tw-py-3 tw-text-[11px] tw-text-gray-500 tw-whitespace-nowrap">
                                            {entry.invoice_no && <div>Inv: {entry.invoice_no}</div>}
                                            {entry.check_no && <div>Chq: {entry.check_no}</div>}
                                            {!entry.invoice_no && !entry.check_no && <span className="tw-text-gray-300">—</span>}
                                        </td>
                                        <td className="tw-px-4 tw-py-3 tw-text-right tw-font-medium">
                                            {parseFloat(entry.debit_amount) > 0 ? <span className="tw-text-emerald-600">{formatCurrency(entry.debit_amount)}</span> : <span className="tw-text-gray-300">—</span>}
                                        </td>
                                        <td className="tw-px-4 tw-py-3 tw-text-right tw-font-medium">
                                            {parseFloat(entry.credit_amount) > 0 ? <span className="tw-text-rose-600">{formatCurrency(entry.credit_amount)}</span> : <span className="tw-text-gray-300">—</span>}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="tw-text-center tw-py-12">
                                        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                                            <Icon icon="hugeicons:invoice-03" className="tw-text-3xl tw-text-gray-300" />
                                            <p className="tw-text-xs tw-text-gray-400">No transactions found</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Journal voucher view — opens when a transaction row is clicked. */}
            {viewJournalId && (
                <ViewJournalModal
                    journalId={viewJournalId}
                    onClose={() => setViewJournalId(null)}
                />
            )}
        </div>
    );
};

export default BankBook;
